/**
 * Shared utilities for the PDO/Garden admin views.
 */
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useClientStore } from '@/stores/client';

/**
 * Resolve the active clientUid for an ontology/garden route. The URL is the
 * source of truth; if the URL is missing it, fall back to the global client
 * store and redirect there. When the global store changes to a different
 * client, the URL is updated in-place so the current sub-route is preserved.
 */
export function useResolvedClientUid(routePrefix: string): string | null {
  const { clientUid } = useParams<{ clientUid: string }>();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const storeUid = useClientStore((s) => s.selectedClientUid);
  const clients = useClientStore((s) => s.clients);
  const fetchClients = useClientStore((s) => s.fetchClients);

  useEffect(() => {
    if (clients.length === 0) void fetchClients();
  }, [clients.length, fetchClients]);

  // No clientUid in URL — redirect to the store/first client.
  useEffect(() => {
    if (clientUid) return;
    const fallback = storeUid ?? clients[0]?.client_uid;
    if (fallback) navigate(`${routePrefix}/${fallback}`, { replace: true });
  }, [clientUid, storeUid, clients, navigate, routePrefix]);

  // Store changed to a different client — swap the clientUid segment in the
  // current path so the sub-route (e.g. /runs, /briefs) is preserved.
  useEffect(() => {
    if (!storeUid || !clientUid || storeUid === clientUid) return;
    const newPath = pathname.replace(
      `${routePrefix}/${clientUid}`,
      `${routePrefix}/${storeUid}`,
    );
    navigate(newPath, { replace: true });
  }, [storeUid, clientUid, pathname, routePrefix, navigate]);

  return clientUid ?? null;
}

/** Format ms as a compact human duration. */
export function formatDuration(ms?: number | null): string {
  if (ms === undefined || ms === null) return '–';
  if (ms < 1000) return `${ms}ms`;
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = s / 60;
  if (m < 60) return `${Math.floor(m)}m ${Math.round(s % 60)}s`;
  const h = m / 60;
  return `${Math.floor(h)}h ${Math.round(m % 60)}m`;
}

/** Parse ISO timestamps; naive UTC strings (no offset) are treated as UTC. */
function parseIsoMs(iso: string): number {
  const trimmed = iso.trim();
  const hasOffset = /[zZ]$|[+-]\d{2}:\d{2}$/.test(trimmed);
  const ts = new Date(hasOffset ? trimmed : `${trimmed}Z`).getTime();
  return ts;
}

/** Relative ago — hours when under 1 day, days after that. */
export function timeAgo(iso?: string | null): string {
  if (!iso) return '–';
  const ts = parseIsoMs(iso);
  if (Number.isNaN(ts)) return iso;
  const s = Math.abs(Date.now() - ts) / 1000;
  if (s < 60) return 'just now';
  if (s < 86_400) {
    const h = Math.max(1, Math.round(s / 3600));
    return `${h}h ago`;
  }
  if (s < 7 * 86_400) {
    const d = Math.max(1, Math.round(s / 86_400));
    return `${d}d ago`;
  }
  return new Date(ts).toLocaleDateString();
}

export function formatNumber(n?: number | null, digits = 0): string {
  if (n === undefined || n === null || Number.isNaN(n)) return '–';
  return n.toLocaleString(undefined, {
    maximumFractionDigits: digits,
    minimumFractionDigits: 0,
  });
}

export function formatDelta(n?: number | null, digits = 2): string {
  if (n === undefined || n === null || Number.isNaN(n)) return '–';
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toFixed(digits)}`;
}

/** Generic data-fetch hook with loading/error/refetch. */
export function useFetch<T>(
  fetcher: () => Promise<T>,
  deps: unknown[],
): {
  data: T | null;
  error: string | null;
  loading: boolean;
  refetch: () => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcher()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : String(e));
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, error, loading, refetch: () => setTick((t) => t + 1) };
}

/** Stable category color palette for the concept graph (6 buckets). */
export const CATEGORY_PALETTE = [
  '#2dd4bf', // teal
  '#a78bfa', // violet
  '#fb923c', // orange
  '#60a5fa', // blue
  '#f472b6', // pink
  '#facc15', // amber
];

export function categoryColor(category: string): string {
  let hash = 0;
  for (let i = 0; i < category.length; i++) {
    hash = (hash * 31 + category.charCodeAt(i)) >>> 0;
  }
  return CATEGORY_PALETTE[hash % CATEGORY_PALETTE.length];
}

export const RELATION_COLORS: Record<string, string> = {
  DRIVES: '#fb923c',
  INHIBITS: '#ef4444',
  CO_OCCURS: '#94a3b8',
  RESOLVES_CONCERN: '#a78bfa',
  CONFIRMS_IDENTITY: '#2dd4bf',
  REMOVES_BARRIER: '#60a5fa',
  SATISFIES: '#f472b6',
  IS_A: '#64748b',
};

export function relationColor(relation: string): string {
  return RELATION_COLORS[relation] ?? '#cbd5e1';
}
